// Quién se lleva cada puesto. No se guarda: sale del cuadro.
//
// Mientras un puesto no esté definido, dice de dónde va a salir ("Ganador de
// la final"), igual que los lugares del cuadro: la página de premios se puede
// mirar desde el primer día y se va completando sola.

import { nombreDeRonda, type CruceResuelto, type CuadroResuelto } from './cuadro';
import { ganador, type Lado as LadoDeMarcador, type Marcador } from './marcador';

export const PUESTOS = [1, 2, 3] as const;
export type Puesto = (typeof PUESTOS)[number];

/**
 * "Pareja campeona" y no "Campeones": la app no sabe si la categoría es de
 * damas, caballeros o mixta, y así vale para todas.
 */
export const NOMBRE_PUESTO: Record<Puesto, string> = {
  1: 'Pareja campeona',
  2: 'Pareja subcampeona',
  3: 'Semifinalistas',
};

export const LARGO_MAXIMO_PREMIO = 80;

/** Una pareja, o el texto de lo que falta para saberla. */
export type EnPodio = { pareja: number } | { pendiente: string };

export interface Podio {
  campeon: EnPodio;
  subcampeon: EnPodio;
  /** Vacío si el cuadro no tiene semifinales (2 parejas) o las dos pasaron directo. */
  semifinalistas: EnPodio[];
  /** La final, para mostrar su resultado. */
  final: CruceResuelto;
}

const pareja = (l: CruceResuelto['a']): number | null => ('pareja' in l ? l.pareja : null);

/** Quién perdió un cruce ya resuelto (jugado o por W.O.). null si todavía no hay resultado. */
function perdedor(c: CruceResuelto): number | null {
  if (!c.marcador) return null;
  return pareja(ganador(c.marcador) === 'a' ? c.b : c.a);
}

export function podioDelCuadro(cuadro: CuadroResuelto): Podio {
  const final = cuadro.rondas[cuadro.rondas.length - 1]![0]!;
  const semis = cuadro.rondas.length >= 2 ? cuadro.rondas[cuadro.rondas.length - 2]! : [];
  const perdioLaFinal = perdedor(final);

  return {
    campeon: cuadro.campeon !== null ? { pareja: cuadro.campeon } : { pendiente: 'Ganador de la final' },
    subcampeon: perdioLaFinal !== null ? { pareja: perdioLaFinal } : { pendiente: 'Perdedor de la final' },
    semifinalistas: semis
      .filter((c) => !c.pasaDirecto)
      .map((c) => {
        const p = perdedor(c);
        return p !== null ? { pareja: p } : { pendiente: `Perdedor semi ${c.pos + 1}` };
      }),
    final,
  };
}

export interface PasoDelCamino {
  /** "Cuartos", "Semifinales", "Final" */
  ronda: string;
  rival: number;
  marcador: Marcador;
  /** De qué lado del marcador jugó la pareja: para mostrar sus games primero. */
  desde: LadoDeMarcador;
}

/** Los partidos que jugó una pareja en el cuadro, en orden: el camino del campeón. */
export function caminoEnElCuadro(cuadro: CuadroResuelto, parejaId: number): PasoDelCamino[] {
  const pasos: PasoDelCamino[] = [];
  for (const ronda of cuadro.rondas) {
    for (const c of ronda) {
      if (c.pasaDirecto || !c.marcador) continue;
      const a = pareja(c.a);
      const b = pareja(c.b);
      if (a === null || b === null) continue;
      if (a === parejaId) pasos.push({ ronda: nombreDeRonda(ronda.length), rival: b, marcador: c.marcador, desde: 'a' });
      else if (b === parejaId) pasos.push({ ronda: nombreDeRonda(ronda.length), rival: a, marcador: c.marcador, desde: 'b' });
    }
  }
  return pasos;
}

/** Valida lo que manda el organizador: un texto por puesto, vacío = sin premio. */
export function validarPremios(cuerpo: unknown): { ok: true; valor: Record<Puesto, string> } | { ok: false; error: string } {
  const c = typeof cuerpo === 'object' && cuerpo !== null ? (cuerpo as Record<string, unknown>) : {};
  const premios = typeof c.premios === 'object' && c.premios !== null ? (c.premios as Record<string, unknown>) : null;
  if (!premios) return { ok: false, error: 'Faltan los premios.' };
  const valor = { 1: '', 2: '', 3: '' } as Record<Puesto, string>;
  for (const puesto of PUESTOS) {
    const texto = premios[puesto] ?? '';
    if (typeof texto !== 'string') return { ok: false, error: 'Cada premio es un texto.' };
    const limpio = texto.replace(/\s+/g, ' ').trim();
    if (limpio.length > LARGO_MAXIMO_PREMIO) return { ok: false, error: `Un premio es muy largo (máx. ${LARGO_MAXIMO_PREMIO}).` };
    valor[puesto] = limpio;
  }
  return { ok: true, valor };
}
